'use client';

import { useEffect, useRef } from 'react';
import { HexAlphaColorPicker } from 'react-colorful';
import { cn } from '../../../../lib/utils';
import { useColorPickerContext } from './color-picker-context';

export interface ColorPickerAreaProps {
  className?: string;
}

const splitHexAndAlpha = (
  hex: string,
  fallbackOpacity: number,
): { baseHex: string; alpha: number } => {
  const normalized = hex.toUpperCase();
  if (/^#[0-9A-F]{8}$/.test(normalized)) {
    const baseHex = normalized.slice(0, 7);
    const alpha = Number.parseInt(normalized.slice(7, 9), 16) / 255;
    return { baseHex, alpha };
  }
  if (/^#[0-9A-F]{4}$/.test(normalized)) {
    const r = normalized[1]!;
    const g = normalized[2]!;
    const b = normalized[3]!;
    const baseHex = `#${r}${r}${g}${g}${b}${b}`;
    const alpha = Number.parseInt(`${normalized[4]}${normalized[4]}`, 16) / 255;
    return { baseHex, alpha };
  }
  return { baseHex: normalized.slice(0, 7), alpha: fallbackOpacity };
};

// 모듈 레벨 상수로 호이스팅하여 매 렌더마다 재생성 방지
const AREA_STYLES = `
.color-picker-area-scope .react-colorful {
  width: 100%;
  gap: 0;
  overflow: visible;
}
.color-picker-area-scope .react-colorful__saturation {
  width: 100% !important;
  height: auto !important;
  padding-bottom: 100% !important;
  border-radius: 6px;
}
.color-picker-area-scope .react-colorful__saturation-pointer {
  width: 24px;
  height: 24px;
  border: 2px solid #ffffff;
  box-shadow: 0px 0px 8px 0px rgba(4, 4, 5, 0.16);
  border-radius: 9999px;
  transform: translate(-50%, -50%);
}
.color-picker-area-scope .react-colorful__hue,
.color-picker-area-scope .react-colorful__alpha {
  margin-top: 12px;
  height: 14px;
  border-radius: 9999px;
  overflow: visible;
  outline: 1px solid var(--sys-style-neutral-light);
  outline-offset: -1px;
}
.color-picker-area-scope .react-colorful__hue-pointer,
.color-picker-area-scope .react-colorful__alpha-pointer {
  width: 24px;
  height: 24px;
  border: 2px solid #ffffff;
  box-shadow: 0px 0px 8px 0px rgba(4, 4, 5, 0.16);
  border-radius: 9999px;
  transform: translate(-50%, -50%);
}
.color-picker-area-scope .react-colorful__alpha {
  background-image:
    linear-gradient(45deg, rgba(0,0,0,0.12) 25%, transparent 25%),
    linear-gradient(-45deg, rgba(0,0,0,0.12) 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, rgba(0,0,0,0.12) 75%),
    linear-gradient(-45deg, transparent 75%, rgba(0,0,0,0.12) 75%) !important;
  background-size: 10px 10px !important;
  background-position: 0 0, 0 5px, 5px -5px, -5px 0px !important;
}
`;

const normalizeBaseHex = (color: string): string => {
  const normalized = color.toUpperCase();
  if (/^#[0-9A-F]{8}$/.test(normalized)) {
    return normalized.slice(0, 7);
  }
  if (/^#[0-9A-F]{6}$/.test(normalized)) {
    return normalized;
  }
  return '#FFFFFF';
};

export function ColorPickerArea({ className }: ColorPickerAreaProps) {
  const {
    mode,
    value,
    isNone,
    opacity,
    onOpacityChange,
    onChangeEnd,
    onOpacityChangeEnd,
    lastSolidColor,
    onChange,
    setLastSolidColor,
  } = useColorPickerContext();

  const containerRef = useRef<HTMLDivElement>(null);
  const onOpacityChangeRef = useRef(onOpacityChange);
  onOpacityChangeRef.current = onOpacityChange;
  const onChangeEndRef = useRef(onChangeEnd);
  onChangeEndRef.current = onChangeEnd;
  const onOpacityChangeEndRef = useRef(onOpacityChangeEnd);
  onOpacityChangeEndRef.current = onOpacityChangeEnd;

  const isDraggingRef = useRef(false);
  const lastColorRef = useRef<string | null>(value);
  const lastOpacityRef = useRef(opacity);

  useEffect(() => {
    lastColorRef.current = value;
  }, [value]);

  useEffect(() => {
    lastOpacityRef.current = opacity;
  }, [opacity]);

  useEffect(() => {
    if (mode !== 'solid') return;
    const container = containerRef.current;
    if (!container) return;

    let isDraggingAlpha = false;

    const isInAlphaTrack = (target: EventTarget | null): boolean => {
      const track = container.querySelector('.react-colorful__alpha');
      return !!track && track.contains(target as Node);
    };

    const onStart = (e: Event) => {
      isDraggingRef.current = true;
      isDraggingAlpha = isInAlphaTrack(e.target);
    };

    const handleDragEnd = (clientX?: number) => {
      // alpha 경계 스냅 (빠른 드래그 시 끝에 도달하지 못하는 경우 보정)
      if (isDraggingAlpha && clientX != null) {
        const track = container.querySelector(
          '.react-colorful__alpha',
        ) as HTMLElement | null;
        if (track) {
          const rect = track.getBoundingClientRect();
          if (rect.width > 0) {
            const ratio = (clientX - rect.left) / rect.width;
            if (ratio >= 0.97) onOpacityChangeRef.current(1);
            else if (ratio <= 0.03) onOpacityChangeRef.current(0);
          }
        }
      }

      isDraggingAlpha = false;

      if (isDraggingRef.current) {
        isDraggingRef.current = false;
        onChangeEndRef.current?.(lastColorRef.current);
        onOpacityChangeEndRef.current?.(lastOpacityRef.current);
      }
    };

    const onMouseUp = (e: MouseEvent) => handleDragEnd(e.clientX);
    const onTouchEnd = (e: TouchEvent) => {
      const touch = e.changedTouches[0];
      handleDragEnd(touch?.clientX);
    };

    container.addEventListener('mousedown', onStart, true);
    container.addEventListener('touchstart', onStart, true);
    window.addEventListener('mouseup', onMouseUp);
    window.addEventListener('touchend', onTouchEnd);

    return () => {
      container.removeEventListener('mousedown', onStart, true);
      container.removeEventListener('touchstart', onStart, true);
      window.removeEventListener('mouseup', onMouseUp);
      window.removeEventListener('touchend', onTouchEnd);
    };
  }, [mode]);

  if (mode !== 'solid') {
    return null;
  }

  const fallbackColor = lastSolidColor || '#FFFFFF';
  const displayColor = normalizeBaseHex(
    value && value.startsWith('#') ? value : fallbackColor,
  );
  const alphaHex = Math.round(opacity * 255)
    .toString(16)
    .padStart(2, '0')
    .toUpperCase();
  const pickerColor = `${displayColor}${alphaHex}`;

  return (
    <div
      ref={containerRef}
      className={cn(
        'color-picker-area-scope relative w-full px-[5px]',
        isNone && 'pointer-events-none opacity-50',
        className,
      )}
    >
      <style>{AREA_STYLES}</style>
      <HexAlphaColorPicker
        className="h-full w-full"
        style={{ width: '100%', height: '100%' }}
        color={pickerColor}
        onChange={(nextColor) => {
          const { baseHex, alpha } = splitHexAndAlpha(nextColor, opacity);

          setLastSolidColor(baseHex);
          onChange(baseHex);
          lastColorRef.current = baseHex;

          const raw255 = Math.round(alpha * 255);
          const snapped255 = raw255 >= 252 ? 255 : raw255 <= 3 ? 0 : raw255;
          const prev255 = Math.round(opacity * 255);

          if (snapped255 === prev255) return;

          const isAtBoundary = snapped255 === 0 || snapped255 === 255;
          const colorChanged = baseHex !== displayColor;
          const isRoundtripNoise =
            !isAtBoundary && colorChanged && Math.abs(raw255 - prev255) <= 3;

          if (!isRoundtripNoise) {
            const newOpacity = snapped255 / 255;
            onOpacityChange(newOpacity);
            lastOpacityRef.current = newOpacity;
          }
        }}
      />
    </div>
  );
}
